import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { getSearchSubmitError } from '../searchSubmit';
import {
  addRecentSearch,
  getRecentSearches,
  removeRecentSearch,
  subscribeRecentSearches,
} from '../recentSearches';
import {
  browsePathFromSuggestion,
  findExactBrowsePath,
  isBrowseSuggestion,
  preloadBrowseCategories,
} from '../categoryBrowseMatch';
import { trackSearch, trackSearchAutocompleteSelect } from '../ga';
import './SearchBar.css';

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

function suggestionQuery(phrase) {
  return phrase.replace(/\s+$/u, '').trim();
}

/** True when the box has user text worth fetching autocomplete for. */
function hasAutocompletePrefix(query) {
  return query.replace(/^\s+/u, '').length >= 1;
}

function InsertWithoutSearchIcon() {
  return (
    <svg
      className="search-bar__suggestion-insert-icon"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 12 3 3M3 3v4.5M3 3h4.5"
      />
    </svg>
  );
}

function BrowseCategoryArrowIcon() {
  return (
    <svg
      className="search-bar__suggestion-browse-icon"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.5 7h9M7.5 3.5 11 7 7.5 10.5"
      />
    </svg>
  );
}

function RecentSearchIcon() {
  return (
    <svg
      className="search-bar__suggestion-recent-icon"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 3.5v3.25l2.25 1.35M7 1.75a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Z"
      />
    </svg>
  );
}

function CategoryBrowseIcon() {
  return (
    <svg
      className="search-bar__suggestion-category-icon"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
    >
      <path d="M0 0h14v14H0z" fill="none" />
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m13.5 13.5l-1.778-1.778M3.903 5.816q-.35.027-.708.028c-.474 0-.939-.043-1.392-.094A1.326 1.326 0 0 1 .638 4.582C.59 4.132.55 3.67.55 3.198c0-.47.041-.933.09-1.383c.064-.61.555-1.1 1.164-1.168c.453-.05.918-.094 1.392-.094s.939.043 1.391.094c.61.068 1.1.558 1.165 1.168c.049.45.09.912.09 1.383q-.001.336-.025.666M3.195 8.155c-.474 0-.939.044-1.392.094c-.61.068-1.1.559-1.165 1.168c-.048.45-.089.913-.089 1.384s.041.933.09 1.384c.064.61.555 1.1 1.164 1.168c.453.05.918.094 1.392.094s.939-.044 1.391-.094M7.931 3.198c0-.471.04-.933.089-1.383c.065-.61.556-1.1 1.165-1.168c.453-.05.918-.094 1.392-.094s.938.043 1.391.094c.61.068 1.1.558 1.165 1.168c.048.45.09.912.09 1.383c0 .472-.042.934-.09 1.384a1.3 1.3 0 0 1-.11.4m-4.199 7.711c2.479 0 3.873-1.394 3.873-3.873s-1.394-3.873-3.873-3.873S4.951 6.34 4.951 8.82s1.394 3.873 3.873 3.873"
      />
    </svg>
  );
}

function normalizeSuggestionKey(phrase) {
  return typeof phrase === 'string' ? phrase.trim().toLocaleLowerCase('en') : '';
}

function autocompletePrefix(displayValue) {
  return displayValue.replace(/^\s+/u, '');
}

/**
 * @param {{ query: string, searchedAt: number }[]} recentSearches
 * @param {Array<{ phrase: string, kind?: string, categorySlug?: string, subcategorySlug?: string }>} apiSuggestions
 * @param {string} apiSuggestionsPrefix
 * @param {string} displayValue
 */
function buildDisplaySuggestions(
  recentSearches,
  apiSuggestions,
  apiSuggestionsPrefix,
  displayValue,
) {
  const prefix = autocompletePrefix(displayValue);
  const prefixLower = prefix.toLocaleLowerCase('en');

  const recentRows = recentSearches
    .filter(
      (entry) =>
        prefix === ''
        || entry.query.toLocaleLowerCase('en').startsWith(prefixLower),
    )
    .map((entry) => ({ phrase: entry.query, isRecent: true, isBrowse: false }));

  const recentKeys = new Set(
    recentRows.map((row) => normalizeSuggestionKey(row.phrase)),
  );

  const apiRows = [];

  if (hasAutocompletePrefix(displayValue) && apiSuggestionsPrefix === prefixLower) {
    for (const row of apiSuggestions) {
      const phraseKey = normalizeSuggestionKey(row.phrase);
      if (phraseKey === '' || recentKeys.has(phraseKey)) {
        continue;
      }

      apiRows.push({
        phrase: row.phrase,
        isRecent: false,
        isBrowse: isBrowseSuggestion(row),
        browsePath: isBrowseSuggestion(row) ? browsePathFromSuggestion(row) : null,
        kind: row.kind,
      });
      recentKeys.add(phraseKey);
    }
  }

  return [...recentRows, ...apiRows];
}

export function SearchField({
  id = 'site-search',
  placeholder = 'Search products…',
  label = 'Search products',
  srLabelClass = 'sr-only',
  defaultValue = '',
  value,
  onChange,
  onSubmitSearch,
  className,
  autocompleteEnabled = true,
  scopePill = null,
  searchSubmitScope = {},
  gaSearchLocation = 'unknown',
}) {
  const navigate = useNavigate();
  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue ?? '');
  const displayValue = isControlled ? value : innerValue;
  const listboxId = useId();
  const hintId = `${id}-hint`;
  const inputRef = useRef(null);
  const blurCloseTimer = useRef(null);
  const autocompleteFetchPrefixRef = useRef('');

  const [recentSearches, setRecentSearches] = useState(() => getRecentSearches());
  const [apiSuggestions, setApiSuggestions] = useState([]);
  const [apiSuggestionsPrefix, setApiSuggestionsPrefix] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [autocompleteDismissed, setAutocompleteDismissed] = useState(false);
  const [submitHint, setSubmitHint] = useState('');

  const displaySuggestions = useMemo(
    () =>
      buildDisplaySuggestions(
        recentSearches,
        apiSuggestions,
        apiSuggestionsPrefix,
        displayValue,
      ),
    [apiSuggestions, apiSuggestionsPrefix, displayValue, recentSearches],
  );

  const setDisplayValue = useCallback(
    (next) => {
      if (!isControlled) {
        setInnerValue(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  const clearBlurTimer = useCallback(() => {
    if (blurCloseTimer.current != null) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
  }, []);

  const closeSuggestions = useCallback(() => {
    autocompleteFetchPrefixRef.current = '';
    setApiSuggestions([]);
    setApiSuggestionsPrefix('');
    setHighlightIndex(-1);
  }, []);

  /** Put phrase in the box with trailing space; do not run search. */
  const insertPhraseOnly = useCallback(
    (phrase) => {
      const next = `${phrase.replace(/\s+$/u, '')} `;
      setDisplayValue(next);
      closeSuggestions();
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [closeSuggestions, setDisplayValue],
  );

  const trySubmitSearch = useCallback(
    (query) => {
      const error = getSearchSubmitError(query, searchSubmitScope);
      if (error) {
        setSubmitHint(error);
        requestAnimationFrame(() => inputRef.current?.focus());
        return false;
      }
      setSubmitHint('');
      trackSearch(query, gaSearchLocation);
      addRecentSearch(query);
      onSubmitSearch?.(query);
      return true;
    },
    [gaSearchLocation, onSubmitSearch, searchSubmitScope],
  );

  const submitQuery = useCallback(
    async (query) => {
      await preloadBrowseCategories();
      const browsePath = findExactBrowsePath(query);
      if (browsePath) {
        setSubmitHint('');
        setAutocompleteDismissed(true);
        closeSuggestions();
        navigate(browsePath);
        return;
      }
      trySubmitSearch(query);
    },
    [closeSuggestions, navigate, trySubmitSearch],
  );

  /** Navigate to a category or subcategory page. */
  const browseWithSuggestion = useCallback(
    (row) => {
      if (!row?.browsePath) {
        return;
      }
      setAutocompleteDismissed(true);
      closeSuggestions();
      setDisplayValue(row.phrase);
      trackSearchAutocompleteSelect();
      navigate(row.browsePath);
    },
    [closeSuggestions, navigate, setDisplayValue],
  );

  /** Run search for this phrase (primary row / Enter). */
  const activateSuggestion = useCallback(
    (row) => {
      if (row?.isBrowse) {
        browseWithSuggestion(row);
        return;
      }
      const q = suggestionQuery(row.phrase);
      setAutocompleteDismissed(true);
      closeSuggestions();
      setDisplayValue(q);
      trackSearchAutocompleteSelect();
      trySubmitSearch(q);
    },
    [browseWithSuggestion, closeSuggestions, setDisplayValue, trySubmitSearch],
  );

  useEffect(() => subscribeRecentSearches(setRecentSearches), []);

  useEffect(() => {
    preloadBrowseCategories();
  }, []);

  useEffect(() => {
    if (!autocompleteEnabled) {
      closeSuggestions();
      return undefined;
    }
    if (!isFocused) {
      return undefined;
    }
    if (autocompleteDismissed || !hasAutocompletePrefix(displayValue)) {
      return undefined;
    }

    const fetchPrefix = autocompletePrefix(displayValue);
    const fetchPrefixLower = fetchPrefix.toLocaleLowerCase('en');
    autocompleteFetchPrefixRef.current = fetchPrefixLower;

    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const u = new URL(
          '/api/search-autocomplete.php',
          window.location.origin,
        );
        u.searchParams.set('prefix', fetchPrefix);
        const res = await fetch(u.toString(), { signal: ac.signal });
        if (!res.ok) {
          throw new Error('autocomplete');
        }
        const data = await res.json();
        const list = Array.isArray(data.suggestions) ? data.suggestions : [];
        if (
          ac.signal.aborted
          || autocompleteFetchPrefixRef.current !== fetchPrefixLower
        ) {
          return;
        }
        setApiSuggestionsPrefix(fetchPrefixLower);
        setApiSuggestions(list);
        setHighlightIndex(-1);
      } catch {
        if (
          !ac.signal.aborted
          && autocompleteFetchPrefixRef.current === fetchPrefixLower
        ) {
          setApiSuggestionsPrefix(fetchPrefixLower);
          setApiSuggestions([]);
          setHighlightIndex(-1);
        }
      }
    }, 200);

    return () => {
      ac.abort();
      window.clearTimeout(t);
    };
  }, [
    autocompleteDismissed,
    autocompleteEnabled,
    closeSuggestions,
    displayValue,
    isFocused,
  ]);

  const listOpen =
    autocompleteEnabled
    && isFocused
    && !autocompleteDismissed
    && displaySuggestions.length > 0;

  const activeDescendantId =
    listOpen && highlightIndex >= 0 && displaySuggestions[highlightIndex]
      ? `${listboxId}-opt-${highlightIndex}`
      : undefined;

  function handleInputChange(e) {
    setAutocompleteDismissed(false);
    setSubmitHint('');
    const v = e.target.value;
    if (!isControlled) {
      setInnerValue(v);
    }
    onChange?.(v);
  }

  function handleInputKeyDown(e) {
    if (!listOpen) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => {
        if (i < 0) {
          return 0;
        }
        return Math.min(displaySuggestions.length - 1, i + 1);
      });
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => {
        if (i <= 0) {
          return -1;
        }
        return i - 1;
      });
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setAutocompleteDismissed(true);
      closeSuggestions();
      return;
    }
    if (e.key === 'Enter' && highlightIndex >= 0) {
      const row = displaySuggestions[highlightIndex];
      if (row?.phrase) {
        e.preventDefault();
        activateSuggestion(row);
      }
    }
  }

  function handleInputBlur() {
    clearBlurTimer();
    blurCloseTimer.current = window.setTimeout(() => {
      setIsFocused(false);
      closeSuggestions();
    }, 150);
  }

  function handleInputFocus() {
    clearBlurTimer();
    setAutocompleteDismissed(false);
    setIsFocused(true);
  }

  function handleSuggestionMouseDown(e) {
    e.preventDefault();
    clearBlurTimer();
  }

  return (
    <form
      className={cx('search-bar', className)}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const q = (fd.get('q') ?? '').toString();
        setAutocompleteDismissed(true);
        closeSuggestions();
        void submitQuery(q);
      }}
    >
      <label htmlFor={id} className={cx('search-bar__label', srLabelClass)}>
        {label}
      </label>
      <div className="search-bar__combo">
        <div
          className={cx(
            'search-bar__row',
            scopePill && 'search-bar__row--has-pill',
          )}
        >
          <input
            ref={inputRef}
            id={id}
            name="q"
            type="search"
            className="search-bar__input"
            placeholder={placeholder}
            autoComplete="off"
            enterKeyHint="search"
            aria-autocomplete={autocompleteEnabled ? 'list' : undefined}
            aria-expanded={listOpen}
            aria-controls={listOpen ? listboxId : undefined}
            aria-activedescendant={activeDescendantId}
            aria-invalid={submitHint ? true : undefined}
            aria-describedby={submitHint ? hintId : undefined}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            value={displayValue}
            onChange={handleInputChange}
          />
          {scopePill && (
            <span className="search-bar__scope-pill">
              <span className="search-bar__scope-pill-label">
                {scopePill.label}
              </span>
              {typeof scopePill.onRemove === 'function' && (
                <button
                  type="button"
                  className="search-bar__scope-pill-remove"
                  aria-label={`Remove ${scopePill.label} filter`}
                  onClick={(e) => {
                    e.preventDefault();
                    scopePill.onRemove();
                  }}
                >
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </span>
          )}
          <button
            type="submit"
            className="search-bar__submit"
            aria-label="Search"
          >
            Search
          </button>
        </div>
        {submitHint ? (
          <p id={hintId} className="search-bar__hint" role="alert">
            {submitHint}
          </p>
        ) : null}
        {listOpen && (
          <ul
            id={listboxId}
            className="search-bar__suggestions"
            role="listbox"
            aria-label="Search suggestions"
          >
            {displaySuggestions.map((row, idx) => (
              <li
                key={`${row.isRecent ? 'recent' : row.isBrowse ? `browse:${row.kind}` : 'api'}:${row.phrase}`}
                className={cx(
                  'search-bar__suggestion-row',
                  row.isRecent && 'search-bar__suggestion-row--recent',
                  row.isBrowse && 'search-bar__suggestion-row--browse',
                  highlightIndex === idx && 'search-bar__suggestion-row--active',
                )}
                role="presentation"
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                <button
                  type="button"
                  id={`${listboxId}-opt-${idx}`}
                  role="option"
                  aria-selected={highlightIndex === idx}
                  className={cx(
                    'search-bar__suggestion-pick',
                    row.isRecent && 'search-bar__suggestion-pick--recent',
                    row.isBrowse && 'search-bar__suggestion-pick--browse',
                  )}
                  onMouseDown={handleSuggestionMouseDown}
                  onClick={() => activateSuggestion(row)}
                >
                  {row.isRecent ? <RecentSearchIcon /> : null}
                  {row.isBrowse ? <CategoryBrowseIcon /> : null}
                  <span className="search-bar__suggestion-word">{row.phrase}</span>
                </button>
                {row.isRecent ? (
                  <button
                    type="button"
                    className="search-bar__suggestion-remove"
                    tabIndex={-1}
                    aria-label={`Remove “${row.phrase}” from recent searches`}
                    onMouseDown={handleSuggestionMouseDown}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRecentSearch(row.phrase);
                      requestAnimationFrame(() => {
                        inputRef.current?.focus();
                      });
                    }}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                ) : row.isBrowse ? (
                  <button
                    type="button"
                    className="search-bar__suggestion-browse"
                    tabIndex={-1}
                    aria-label={`Go to ${row.phrase} category`}
                    onMouseDown={handleSuggestionMouseDown}
                    onClick={(e) => {
                      e.stopPropagation();
                      browseWithSuggestion(row);
                    }}
                  >
                    <BrowseCategoryArrowIcon />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="search-bar__suggestion-insert"
                    tabIndex={-1}
                    aria-label={`Insert “${row.phrase}” into the search box without searching`}
                    onMouseDown={handleSuggestionMouseDown}
                    onClick={(e) => {
                      e.stopPropagation();
                      insertPhraseOnly(row.phrase);
                    }}
                  >
                    <InsertWithoutSearchIcon />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </form>
  );
}
